import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import Loader from "components/Loader";
import CommittedDateHeader from "./CommittedDateHeader";
import CommittedItemsTable from "./CommittedItemsTable";
import { mockDeliveryDateData } from "./mockData";
import { mapOrderEddResponse } from "./eddOrderMapper";
import { fetchOrderEddDetails } from "services/eddService";

const DeliveryDateTab = () => {
  const { id: routeOrderId } = useParams();
  const orderDetails = useSelector((state) => state.orderDetails);

  const orderNumber = routeOrderId || orderDetails?.orderNumber;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [eddData, setEddData] = useState(null);

  const loadOrderEdd = useCallback(async () => {
    if (!orderNumber) return;

    setIsLoading(true);
    setError(null);

    const result = await fetchOrderEddDetails(orderNumber);

    setIsLoading(false);

    if (result.success && result.data) {
      const mapped = mapOrderEddResponse(result.data, orderDetails);
      setEddData(mapped);
    } else {
      setError(result.error || "Unable to fetch delivery date for this order");
    }
  }, [orderNumber, orderDetails]);

  useEffect(() => {
    void loadOrderEdd();
  }, [loadOrderEdd]);

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight={320}
        gap={2}
      >
        <Loader size={36} />
        <Typography fontSize={14} color="text.secondary">
          Loading estimated delivery date details...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void loadOrderEdd();
              }}
            >
              Retry
            </Button>
          }
          sx={{ borderRadius: "12px" }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // If no EDD was returned or EDD is not available for this order
  if (eddData?.eddAvailable === false) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight={320}
        p={4}
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E5E7EB",
          borderRadius: "16px",
          textAlign: "center",
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 48, color: "#9CA3AF", mb: 1.5 }} />
        <Typography variant="h6" fontWeight={700} color="text.primary" mb={1}>
          Delivery Date Not Available
        </Typography>
        <Typography fontSize={14} color="text.secondary" maxWidth={450}>
          Estimated Delivery Date calculation is currently not available for order{" "}
          <strong>{orderNumber}</strong>.
        </Typography>
      </Box>
    );
  }

  const headerData = eddData?.header || mockDeliveryDateData;
  const itemsData = eddData?.items?.length
    ? eddData.items
    : mockDeliveryDateData.items;
  const formulaNote =
    eddData?.processingFormulaNote || mockDeliveryDateData.processingFormulaNote;

  return (
    <Box
      sx={{
        p: { xs: 1, md: 2 },
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      <CommittedDateHeader data={headerData} />
      <CommittedItemsTable
        items={itemsData}
        formulaNote={formulaNote}
      />
    </Box>
  );
};

export default DeliveryDateTab;
